/**
 * 一覧画面の共通フィルタ行。
 *
 * 値の保持先は URL search params（呼び出し側が `navigate({search})` する）。
 * ここは「入力を受けて上へ流す」だけの制御コンポーネントに徹する。
 *
 * セレクトは Radix ではなく素の `<select>` を使う。`@mail-sv/ui` の
 * `<CursorPagination>` も素の select なので、同じ行に並んだときに高さと質感が揃う。
 */
import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Button, Input, cn } from '@mail-sv/ui';

export interface FilterSelect {
  key: string;
  label: string;
  value: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string | undefined) => void;
  /** 「すべて」の表示名を変えたいとき。 */
  allLabel?: string;
}

export function FilterBar({
  q,
  onQChange,
  placeholder = 'キーワードで絞り込む',
  selects = [],
  onClear,
  active,
  children,
  className,
}: {
  q?: string | undefined;
  onQChange?: (value: string | undefined) => void;
  placeholder?: string;
  selects?: FilterSelect[];
  onClear?: () => void;
  /** フィルタが 1 つでも効いているか。クリアボタンの出し分けに使う。 */
  active?: boolean;
  children?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-panel px-3 py-2.5',
        className,
      )}
    >
      {onQChange ? <SearchInput value={q} onChange={onQChange} placeholder={placeholder} /> : null}
      {selects.map(({ key, ...rest }) => (
        <SelectFilter key={key} filterKey={key} {...rest} />
      ))}
      {children}
      {active && onClear ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <X aria-hidden />
          フィルタをクリア
        </Button>
      ) : null}
    </div>
  );
}

/**
 * 検索欄。1 打鍵ごとにリクエストが飛ぶと cursor がリセットされ続けて操作にならないので、
 * 350ms 止まってから初めて上へ流す。
 */
function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder: string;
}): React.ReactElement {
  const [draft, setDraft] = React.useState(value ?? '');
  const committed = React.useRef(value ?? '');

  // 外側（URL）が変わったら追従する。ブラウザバックで入力欄が置き去りにならないように。
  React.useEffect(() => {
    if ((value ?? '') !== committed.current) {
      committed.current = value ?? '';
      setDraft(value ?? '');
    }
  }, [value]);

  React.useEffect(() => {
    if (draft === committed.current) return;
    const t = setTimeout(() => {
      committed.current = draft;
      onChange(draft.length > 0 ? draft : undefined);
    }, 350);
    return () => clearTimeout(t);
  }, [draft, onChange]);

  return (
    <div className="relative min-w-[14rem] flex-1 sm:max-w-xs">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-9 pl-8"
      />
    </div>
  );
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
  allLabel = 'すべて',
}: Omit<FilterSelect, 'key'> & { filterKey: string }): React.ReactElement {
  const id = React.useId();
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground" htmlFor={id}>
      <span className="whitespace-nowrap">{label}</span>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        className="h-9 rounded-md border border-border bg-panel-elevated px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** 列挙定数 → セレクトの選択肢。ラベルは必ず `labelOf` を通す。 */
export function optionsFrom(
  values: readonly string[],
  labelFn: (v: string) => string,
): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: labelFn(v) }));
}
